/* eslint-disable react/jsx-handler-names */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable react/require-default-props */
// eslint-disable-next-line import/no-extraneous-dependencies
import { cva } from 'class-variance-authority';
import { forwardRef, ReactNode, useImperativeHandle } from 'react';

import useDismissableDialog from '@/hooks/util/useDismissableDialog';

interface DialogBox4Properties {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  variant?: 'default' | 'blue' | 'zero-padding';
  showCloseIcon?: boolean;
  dismissable?: boolean;
  maxDialogWidth?: string;
  title?: ReactNode;
  closeButtonStyle?: 'default' | 'floating';
}

export interface DialogBox4Reference {
  onDismiss: () => void;
}

const dialogVariants = cva('relative flex flex-col items-center justify-center rounded-2xl', {
  variants: {
    variant: {
      default: 'bg-neutral-white px-[2em] py-8 max-w-[733px] w-full',
      blue: 'bg-primary-blue-3 min-w-[43.375rem] border-8 border-secondary-blue-3',
      'zero-padding': 'bg-neutral-white !p-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const childrenContainerVariants = cva('w-full', {
  variants: {
    variant: {
      default: '',
      blue: 'rounded-2xl px-10 pb-10',
      'zero-padding': '!p-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const closeButtonVariants = cva('absolute z-20 flex items-center justify-center rounded-full', {
  variants: {
    style: {
      default: 'right-6 top-6 h-10 w-10',
      floating: '-top-[60px] h-[52px] w-[52px]',
    },
    variant: {
      default: 'bg-white',
      blue: 'border-2 border-neutral-white p-2 bg-primary-blue-3',
      'zero-padding': 'bg-white',
    },
  },
  defaultVariants: {
    style: 'default',
    variant: 'default',
  },
  compoundVariants: [
    {
      variant: 'blue',
      style: 'floating',
      class: 'border-none bg-white',
    },
  ],
});

const DialogBox4 = forwardRef<DialogBox4Reference, DialogBox4Properties>(
  (
    {
      open,
      onClose = () => {},
      children,
      variant = 'default',
      showCloseIcon = true,
      dismissable = true,
      maxDialogWidth,
      title,
      closeButtonStyle = 'default',
    }: DialogBox4Properties,
    reference,
  ) => {
    const { dialogReference, onDismiss } = useDismissableDialog({
      isOpen: open,
      onClose,
      dismissable,
    });

    useImperativeHandle(reference, () => ({
      onDismiss,
    }));

    return (
      <div className="fixed inset-0 z-modal flex h-[100vh] w-[100vw] items-center justify-center backdrop-blur-sm backdrop-brightness-50">
        <dialog
          ref={dialogReference}
          open={open}
          className={dialogVariants({ variant })}
          style={{ maxWidth: maxDialogWidth }}
        >
          {title && (
            <div className="px-8 pt-6 text-center font-lilita text-[2.5rem] text-primary-yellow-3">
              {title}
            </div>
          )}
          {showCloseIcon && (
            <button
              onClick={(event) => {
                event.stopPropagation();
                onDismiss();
              }}
              type="button"
              className={closeButtonVariants({ style: closeButtonStyle, variant })}
            >
              <img
                src={
                  closeButtonStyle === 'floating'
                    ? './icons/cross-icon.svg'
                    : `./icons/cross-icon-${variant === 'blue' ? 'white' : 'grey'}.svg`
                }
                className="h-5 w-5"
                alt="Close"
              />
            </button>
          )}
          <div className={childrenContainerVariants({ variant })}>{children}</div>
        </dialog>
      </div>
    );
  },
);

DialogBox4.displayName = 'DialogBox4';

export default DialogBox4;
